import type { ArbitrageOpportunity } from '../types/opportunity';
import type { HeaderPrices } from '../services/priceWebSocketManager';
import type { ExchangeNetworkStatus } from '../types/networkStatus';

export interface ProfitCalculationResult {
  volumeBeforeFees: number;
  maxExecutableVolume: number;
  maxExecutableVolumeAfterWithdrawal: number;
  totalBuyCost: number;
  totalBuyCostInSellQuote: number;
  buyFeeAmount: number;
  withdrawalFeeAmount: number;
  grossSellRevenue: number;
  sellFeeAmount: number;
  netSellRevenue: number;
  totalFees: number;
  netProfit: number;
  profitPercentage: number;
  buyTakerFee: number;
  sellMakerFee: number;
  withdrawalNetwork: string;
}

/**
 * Calculate profit breakdown for an arbitrage opportunity.
 * This is the exact same calculation logic used in ProfitCalculationTooltip.
 *
 * @param opportunity - The arbitrage opportunity
 * @param maxExecutableVolume - Pre-calculated maximum executable volume (from opportunity.max_volume)
 * @param totalBuyCost - Pre-calculated total buy cost (from opportunity.total_buy_value)
 * @param withdrawalFeeAmount - Withdrawal fee in base currency (default: 0)
 * @param withdrawalNetwork - Network name for withdrawal (default: '')
 * @returns Detailed profit breakdown
 */
export function calculateProfitBreakdown(
  opportunity: ArbitrageOpportunity,
  maxExecutableVolume: number,
  totalBuyCost: number,
  withdrawalFeeAmount: number = 0,
  withdrawalNetwork: string = ''
): ProfitCalculationResult {
  const buyTakerFee = opportunity.buy_fee || 0;
  const sellTakerFee = opportunity.sell_fee || 0; // Use taker fee for sell side too

  // Calculate volume before fees (reverse the fee application)
  const volumeBeforeFees = maxExecutableVolume / (1 - buyTakerFee);

  // Calculate volume after withdrawal fee (same as ProfitCalculationTooltip)
  const maxExecutableVolumeAfterWithdrawal = Math.max(0, maxExecutableVolume - withdrawalFeeAmount);

  // Calculate sell revenue by consuming sell levels (with reduced volume after withdrawal)
  let remainingVolume = maxExecutableVolumeAfterWithdrawal;
  let grossSellRevenue = 0;

  for (const level of opportunity.sell_levels) {
    const volumeAtLevel = Math.min(remainingVolume, level.volume);
    if (volumeAtLevel <= 0) break;

    grossSellRevenue += level.price * volumeAtLevel;
    remainingVolume -= volumeAtLevel;
  }

  // Apply sell taker fee to get net revenue (limit order executed immediately)
  const netSellRevenue = grossSellRevenue * (1 - sellTakerFee);

  // Convert buy cost to sell quote currency for comparison
  const totalBuyCostInSellQuote = opportunity.quote_conversion_rate !== 1.0
    ? totalBuyCost * opportunity.quote_conversion_rate
    : totalBuyCost;

  // Calculate fees
  const buyFeeAmount = totalBuyCost * buyTakerFee;
  const sellFeeAmount = grossSellRevenue * sellTakerFee;
  const totalFees = buyFeeAmount + sellFeeAmount;

  // Calculate profit
  const netProfit = netSellRevenue - totalBuyCostInSellQuote;
  const profitPercentage = totalBuyCostInSellQuote > 0
    ? ((netSellRevenue / totalBuyCostInSellQuote) - 1) * 100
    : 0;

  return {
    volumeBeforeFees,
    maxExecutableVolume,
    maxExecutableVolumeAfterWithdrawal,
    totalBuyCost,
    totalBuyCostInSellQuote,
    buyFeeAmount,
    withdrawalFeeAmount,
    grossSellRevenue,
    sellFeeAmount,
    netSellRevenue,
    totalFees,
    netProfit,
    profitPercentage,
    buyTakerFee,
    sellMakerFee: sellTakerFee, // Return as sellMakerFee for compatibility, but it's actually taker
    withdrawalNetwork,
  };
}

/**
 * Calculate profit breakdown from a budget amount in USD.
 * Consumes buy levels until budget is exhausted, then calculates profit.
 * This limits the volume based on available budget.
 *
 * @param opportunity - The arbitrage opportunity
 * @param budgetUSD - Budget to spend in USD
 * @param prices - Current exchange rates from HeaderStats
 * @param networkStatus - Network status for withdrawal fee calculation (optional)
 * @returns Detailed profit breakdown
 */
export function calculateProfitBreakdownFromBudget(
  opportunity: ArbitrageOpportunity,
  budgetUSD: number,
  prices: HeaderPrices | null,
  networkStatus?: ExchangeNetworkStatus[]
): ProfitCalculationResult {
  const buyTakerFee = opportunity.buy_fee || 0;

  // Convert USD budget to buy quote currency
  // We need the current USD/KRW or USD/BTC exchange rate
  let budgetInBuyQuote = budgetUSD;

  if (opportunity.buy_quote === 'K') {
    // USD to KRW: Use implied rate from Upbit BTC/KRW divided by Binance BTC/USDT
    // This gives us the actual market rate for KRW/USD
    const usdToKrw = prices && prices.upbitBtcKrw && prices.binanceBtcUsdt
      ? prices.upbitBtcKrw / prices.binanceBtcUsdt
      : 1535; // Fallback to approximate rate if prices not available
    budgetInBuyQuote = budgetUSD * usdToKrw;
  } else if (opportunity.buy_quote === 'B') {
    // USD to BTC: Use Binance BTC/USDT price
    const usdToBtc = prices && prices.binanceBtcUsdt
      ? 1 / prices.binanceBtcUsdt
      : 1 / 100000; // Fallback to approximate BTC price
    budgetInBuyQuote = budgetUSD * usdToBtc;
  }
  // For USDT (U), budget is already in USD (~1:1)

  // Calculate how much volume we can buy with the budget (consume buy levels)
  let remainingBudget = budgetInBuyQuote;
  let volumeBeforeFees = 0;
  let totalBuyCost = 0;

  for (const level of opportunity.buy_levels) {
    if (remainingBudget <= 0) break;

    const costAtLevel = level.price * level.volume;
    if (costAtLevel <= remainingBudget) {
      // Can afford entire level
      volumeBeforeFees += level.volume;
      totalBuyCost += costAtLevel;
      remainingBudget -= costAtLevel;
    } else {
      // Partial level
      const volumeAtLevel = remainingBudget / level.price;
      volumeBeforeFees += volumeAtLevel;
      totalBuyCost += remainingBudget;
      remainingBudget = 0;
    }
  }

  // Volume after buy fee (maxExecutableVolume)
  const maxExecutableVolume = volumeBeforeFees * (1 - buyTakerFee);

  // Calculate withdrawal fee (same logic as ProfitCalculationTooltip)
  let withdrawalFeeAmount = 0;
  let withdrawalNetwork = '';

  if (networkStatus && networkStatus.length > 0) {
    const buyExchangeStatus = networkStatus[opportunity.buy_exchange];
    const sellExchangeStatus = networkStatus[opportunity.sell_exchange];

    if (buyExchangeStatus && sellExchangeStatus) {
      const buyCrypto = buyExchangeStatus.cryptos.find(c => c.symbol === opportunity.base);
      const sellCrypto = sellExchangeStatus.cryptos.find(c => c.symbol === opportunity.base);

      if (buyCrypto && sellCrypto) {
        // Find matching networks between buy and sell exchanges
        const matchingNetworks: { network: string; fee: number }[] = [];

        for (const buyNet of buyCrypto.networks) {
          if (!buyNet.withdraw_enabled || buyNet.withdraw_fee === undefined || buyNet.withdraw_fee === null) {
            continue;
          }

          // Find the same network on sell exchange where deposit is enabled
          const sellNet = sellCrypto.networks.find(sn => {
            if (!sn.deposit_enabled) return false;

            // Direct match
            if (sn.network === buyNet.network) return true;

            // Check for common network variations (case-insensitive)
            const buyNetNorm = buyNet.network.toUpperCase();
            const sellNetNorm = sn.network.toUpperCase();

            // ETH variations
            if ((buyNetNorm === 'ETH' || buyNetNorm === 'ERC20' || buyNetNorm === 'ETHEREUM') &&
                (sellNetNorm === 'ETH' || sellNetNorm === 'ERC20' || sellNetNorm === 'ETHEREUM')) {
              return true;
            }

            // BSC variations
            if ((buyNetNorm === 'BSC' || buyNetNorm === 'BEP20' || buyNetNorm === 'BNB') &&
                (sellNetNorm === 'BSC' || sellNetNorm === 'BEP20' || sellNetNorm === 'BNB')) {
              return true;
            }

            // TRON variations
            if ((buyNetNorm === 'TRX' || buyNetNorm === 'TRC20' || buyNetNorm === 'TRON') &&
                (sellNetNorm === 'TRX' || sellNetNorm === 'TRC20' || sellNetNorm === 'TRON')) {
              return true;
            }

            return false;
          });

          if (sellNet) {
            matchingNetworks.push({
              network: buyNet.name || buyNet.network,
              fee: buyNet.withdraw_fee,
            });
          }
        }

        // Use the cheapest matching network
        if (matchingNetworks.length > 0) {
          const cheapest = matchingNetworks.reduce((min, net) =>
            net.fee < min.fee ? net : min
          );
          withdrawalFeeAmount = cheapest.fee;
          withdrawalNetwork = cheapest.network;
        }
      }
    }
  }

  // Calculate volume after withdrawal fee
  const maxExecutableVolumeAfterWithdrawal = Math.max(0, maxExecutableVolume - withdrawalFeeAmount);

  // Calculate sell revenue by consuming sell levels (with reduced volume after withdrawal)
  let remainingVolume = maxExecutableVolumeAfterWithdrawal;
  let grossSellRevenue = 0;

  for (const level of opportunity.sell_levels) {
    const volumeAtLevel = Math.min(remainingVolume, level.volume);
    if (volumeAtLevel <= 0) break;

    grossSellRevenue += level.price * volumeAtLevel;
    remainingVolume -= volumeAtLevel;
  }

  const sellTakerFee = opportunity.sell_fee || 0;

  // Apply sell taker fee to get net revenue (limit order executed immediately)
  const netSellRevenue = grossSellRevenue * (1 - sellTakerFee);

  // Convert buy cost to sell quote currency for comparison
  const totalBuyCostInSellQuote = opportunity.quote_conversion_rate !== 1.0
    ? totalBuyCost * opportunity.quote_conversion_rate
    : totalBuyCost;

  // Calculate fees
  const buyFeeAmount = totalBuyCost * buyTakerFee;
  const sellFeeAmount = grossSellRevenue * sellTakerFee;
  const totalFees = buyFeeAmount + sellFeeAmount + withdrawalFeeAmount;

  // Calculate profit
  const netProfit = netSellRevenue - totalBuyCostInSellQuote;
  const profitPercentage = totalBuyCostInSellQuote > 0
    ? ((netSellRevenue / totalBuyCostInSellQuote) - 1) * 100
    : 0;

  return {
    volumeBeforeFees,
    maxExecutableVolume,
    maxExecutableVolumeAfterWithdrawal,
    totalBuyCost,
    totalBuyCostInSellQuote,
    buyFeeAmount,
    withdrawalFeeAmount,
    grossSellRevenue,
    sellFeeAmount,
    netSellRevenue,
    totalFees,
    netProfit,
    profitPercentage,
    buyTakerFee,
    sellMakerFee: sellTakerFee,
    withdrawalNetwork,
  };
}
